/**
 * AdminStatCard — unified, reusable KPI / metric card for the admin black-glass UI.
 *
 * Replaces scattered Card + inline stat blocks and the analytics/StatCard.
 * Design strictly follows existing tokens (white/[0.0x] rings, Inter, glass surfaces).
 *
 * Features:
 *  - Title + icon header (subtle uppercase)
 *  - Large value (tabular nums)
 *  - Optional trend/delta with up/down color + icon
 *  - Optional hint/subtext
 *  - Loading skeleton state
 *  - Clickable (button or Link) for drill-down
 *  - Accent variant for primary metrics
 *  - Compact variant
 */

import type { LucideIcon } from "lucide-react";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import {
  applyTrendInvert,
  trendDirectionFromValue,
  type AdminTrendDirection,
} from "@/lib/adminTrend";
import { cn } from "@/lib/utils";
import { AdminSkeleton } from "./AdminStates";

export interface AdminStatTrend {
  /** Numeric delta or formatted string (e.g. "+12%" or -4) */
  value: number | string;
  /** Visual direction. If omitted, inferred from numeric value when possible. */
  direction?: AdminTrendDirection;
  /** Optional secondary label e.g. "vs last 7d" */
  label?: string;
  /** When true, growth is bad (e.g. pending backlog). Flips color/icon. */
  invert?: boolean;
}

export interface AdminStatCardProps {
  title: string;
  /** Main metric value. Can be string, number or formatted node. */
  value: ReactNode;
  /** Optional lucide icon or component. */
  icon?: LucideIcon | React.ComponentType<{ className?: string }>;
  /** Trend indicator (delta + direction). */
  trend?: AdminStatTrend;
  /** Small footer hint / description. */
  hint?: string;
  /** Show skeleton instead of content. */
  loading?: boolean;
  /** Make the whole card interactive (navigates internally). */
  href?: string;
  /** onClick handler (used when no href). */
  onClick?: () => void;
  /** Highlight style for primary / important metrics. */
  accent?: boolean;
  /** Smaller padding + value for dense areas. */
  compact?: boolean;
  className?: string;
  /** Optional right-side extra (badge, mini chart slot, etc). */
  action?: ReactNode;
}

function getTrendDirection(trend: AdminStatTrend): AdminTrendDirection {
  const raw = trendDirectionFromValue(trend.value, trend.direction);
  return applyTrendInvert(raw, trend.invert);
}

function getTrendClasses(dir: AdminTrendDirection) {
  if (dir === "up") return "text-emerald-400";
  if (dir === "down") return "text-red-400";
  return "text-white/50";
}

/** Standalone trend pill — reuse outside AdminStatCard (tables, tooltips). */
export function AdminTrendIndicator({
  value,
  direction,
  label,
  invert,
  compact,
  className,
}: {
  value: number | string | null | undefined;
  direction?: AdminTrendDirection;
  label?: string;
  invert?: boolean;
  compact?: boolean;
  className?: string;
}) {
  if (value == null || value === "") return null;
  const dir = applyTrendInvert(
    trendDirectionFromValue(value, direction),
    invert,
  );
  const Icon =
    dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 font-medium",
        getTrendClasses(dir),
        compact ? "text-[10px]" : "text-[11px]",
        className,
      )}
    >
      <Icon className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden />
      <span>{value}</span>
      {label ? <span className="font-normal text-white/35">· {label}</span> : null}
    </span>
  );
}

function TrendIcon({ dir }: { dir: AdminTrendDirection }) {
  const cls = "h-3.5 w-3.5";
  if (dir === "up") return <TrendingUp className={cls} />;
  if (dir === "down") return <TrendingDown className={cls} />;
  return <Minus className={cls} />;
}

export function AdminStatCard({
  title,
  value,
  icon: Icon,
  trend,
  hint,
  loading = false,
  href,
  onClick,
  accent = false,
  compact = false,
  className,
  action,
}: AdminStatCardProps) {
  const dir = trend ? getTrendDirection(trend) : null;
  const trendColor = dir ? getTrendClasses(dir) : "";

  const content = (
    <>
      {/* Header row */}
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35 truncate">
            {title}
          </span>
          {Icon && (
            <span
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-md ring-1 ring-inset shrink-0",
                accent
                  ? "bg-white/10 ring-white/15"
                  : "bg-white/[0.04] ring-white/[0.06]"
              )}
            >
              <Icon
                className={cn(
                  "h-3.5 w-3.5",
                  accent ? "text-indigo-300" : "text-white/45"
                )}
              />
            </span>
          )}
        </div>

        {action && <div className="shrink-0">{action}</div>}
      </div>

      {/* Value */}
      <div
        className={cn(
          "font-semibold leading-none tabular-nums text-white/90",
          compact ? "text-[20px]" : "text-[26px]"
        )}
      >
        {loading ? <AdminSkeleton className="h-7 w-16 rounded" /> : value}
      </div>

      {/* Trend + hint */}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
        {trend && !loading && (
          <span className={cn("inline-flex items-center gap-1 font-medium", trendColor)}>
            <TrendIcon dir={dir!} />
            <span>{trend.value}</span>
            {trend.label && (
              <span className="font-normal text-white/35">· {trend.label}</span>
            )}
          </span>
        )}

        {hint && (
          <span className="text-white/35 truncate">{hint}</span>
        )}
      </div>
    </>
  );

  const isInteractive = Boolean(href || onClick);
  const baseClasses = cn(
    "admin-stat-card group rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4",
    "ui-transition duration-150 ease-out",
    "hover:border-white/[0.12] hover:bg-white/[0.03]",
    // Subtle lift (BudgetForge widget hover) + scale for interactive only; respect reduced-motion.
    "motion-safe:hover:-translate-y-0.5",
    isInteractive &&
      "origin-center cursor-pointer motion-safe:hover:scale-[1.01] motion-safe:active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20",
    accent && "ring-1 ring-inset ring-white/[0.08]",
    compact && "p-3",
    className
  );

  if (href) {
    return (
      <Link to={href} className={baseClasses}>
        {content}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(baseClasses, "text-left")}
      >
        {content}
      </button>
    );
  }

  return <div className={baseClasses}>{content}</div>;
}
